// Пилюля статуса Анны (задача D5, ui-spec §2): «● Тень · работает» в сайдбаре — худшее из «Готовности», рубильника
// и бюджета, как его считает сервер (`GET /api/v1/status`, вердикт D1).
//
// - Текст — «{ступень} · {вердикт с маленькой буквы}». Тон: работает — синяя; экономит и не настроена — янтарная;
//   не работает — красная; остановлена — полая серая.
// - Уже 1280 места меньше, но состояние всё равно словом, а не одним цветом (UX-ревью D5–D7): в верхней полосе —
//   «● Не работает», в сайдбаре иконками — точка и слово под ней; ступень и полный текст — в поповере и тултипе.
// - По нажатию — поповер «Состояние Анны»: вердикт и его строка, ступень, каналы «{канал} — {режим}», «Бюджет за
//   {ДД.ММ}: {x} из {y} ₽», ссылка «Открыть «Запуск»». У админа, пока Анна не остановлена, рядом — «Остановить везде»
//   (D15): тот же запрос `stop_all`, что в «Запуске» (свежая версия с сервера), сразу, без окна; тост «Анна остановлена ·
//   Отменить» (10 с), поповер закрывается. Кнопка тихая — обводка `--destructive`, как в «Запуске».
// - Настройки в базе испорчены (`/status` → 500 `settings_unreadable`) — «Настройки не читаются», красная; в поповере
//   — текст сервера и «Открыть «Готовность»».
// - Чтение с экрана: текст пилюли — в `aria-live="polite"`; переход в «Остановлена» один раз объявляется в
//   `aria-live="assertive"`: «Анна остановлена». Объявляет только главный экземпляр (`announce`), копия в меню
//   телефона — молчит.
import { STAGE_RU, type Status, type StatusTone, type VerdictKind } from '@contract/index.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, messageRuOf } from '@/api/errors';
import { useRole } from '@/api/queries/auth';
import { statusQuery } from '@/api/queries/status';
import { can } from '@/auth/permissions';
import { PendingButton } from '@/components/pending-button';
import { StatusDot, StatusMark } from '@/components/status-dot';
import { buttonVariants } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverDescription, PopoverHeader, PopoverTitle, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { stopEverywhere } from '@/features/launch/changes';
import { STOP_BUTTON_CLASS } from '@/features/launch/stop-block';
import { formatDay, formatInt, formatRub } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useStoppedHereCount } from './stop-announcement';

export const STOPPED_ANNOUNCEMENT_RU = 'Анна остановлена';
export const SETTINGS_UNREADABLE_PILL_RU = 'Настройки не читаются';

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

export interface PillView {
  /** Полный текст: «Тень · не работает». */
  readonly text: string;
  /** Слово состояния для узких мест (верхняя полоса, сайдбар иконками): «Не работает». */
  readonly word: string;
  readonly tone: StatusTone;
  readonly hollow: boolean;
}

/** Тон пилюли по виду вердикта; полая — только «Остановлена». */
const PILL_TONE: Readonly<Record<VerdictKind, { readonly tone: StatusTone; readonly hollow: boolean }>> = {
  working: { tone: 'live', hollow: false },
  economy: { tone: 'human', hollow: false },
  not_configured: { tone: 'human', hollow: false },
  broken: { tone: 'error', hollow: false },
  stopped: { tone: 'reject', hollow: true },
};

/** Вид пилюли по статусу (см. шапку файла). */
export function pillView(status: Status): PillView {
  const stage = STAGE_RU[status.stage];
  const { kind, wordRu } = status.verdict;
  return { text: `${stage} · ${lowerFirst(wordRu)}`, word: wordRu, ...PILL_TONE[kind] };
}

function isSettingsUnreadable(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === 'settings_unreadable';
}

/**
 * Один раз объявляет «Анна остановлена»: когда статус перешёл в «Остановлена» (не при первом чтении) или когда Анну только
 * что остановили из этого окна (`noteStoppedHere`, D15) — тогда сразу, не дожидаясь статуса; пришедший следом статус
 * «Остановлена» текст не меняет, второго объявления нет. Прошлый вид — в состоянии: объявление считается при отрисовке,
 * без эффекта.
 */
function useStoppedAnnouncement(status: Status | undefined): string {
  const kind = status?.verdict.kind;
  const stoppedHere = useStoppedHereCount();
  const [seen, setSeen] = useState<{ readonly kind: VerdictKind | undefined; readonly stoppedHere: number; readonly message: string }>({
    kind,
    stoppedHere,
    message: '',
  });
  if (seen.kind === kind && seen.stoppedHere === stoppedHere) return seen.message;
  let message = seen.message;
  if (seen.stoppedHere !== stoppedHere) message = STOPPED_ANNOUNCEMENT_RU;
  else {
    const becameStopped = seen.kind !== undefined && seen.kind !== 'stopped' && kind === 'stopped';
    if (becameStopped) message = STOPPED_ANNOUNCEMENT_RU;
    else if (kind !== 'stopped') message = '';
  }
  setSeen({ kind, stoppedHere, message });
  return message;
}

export interface StatusPillProps {
  /** `sidebar` — во всю ширину сайдбара; `compact` — точка (сайдбар иконками); `topbar` — точка и ступень. */
  readonly variant: 'sidebar' | 'compact' | 'topbar';
  /** Объявлять ли перемены чтению с экрана: только у главного экземпляра. */
  readonly announce?: boolean;
}

/** «Остановить везде» (D15): остановка сразу, тост с «Отменить»; поповер закрывается, когда запрос закончился. */
function StopEverywhereButton({ onDone }: { readonly onDone: () => void }) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  return (
    <PendingButton
      pending={pending}
      variant="outline"
      size="sm"
      className={cn(STOP_BUTTON_CLASS, 'max-sm:h-10')}
      onClick={() => {
        setPending(true);
        void stopEverywhere(queryClient).finally(() => {
          setPending(false);
          onDone();
        });
      }}
    >
      Остановить везде
    </PendingButton>
  );
}

function StatusDetails({ status, onClose }: { readonly status: Status; readonly onClose: () => void }) {
  const { budget, channels, verdict } = status;
  const view = pillView(status);
  const role = useRole();
  const canStop = role !== undefined && can(role, 'launch') && status.stopped === null && verdict.kind !== 'stopped';
  return (
    <>
      <PopoverHeader>
        <PopoverTitle>Состояние Анны</PopoverTitle>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <StatusDot tone={view.tone} hollow={view.hollow} label={verdict.wordRu} strong />
          <span className="text-sm text-muted-foreground">{`Ступень — ${STAGE_RU[status.stage]}`}</span>
        </div>
        {verdict.headlineRu !== null && <PopoverDescription>{verdict.headlineRu}</PopoverDescription>}
      </PopoverHeader>
      {channels.length > 0 && (
        <ul aria-label="Каналы" className="flex flex-col gap-1">
          {channels.map((channel) => (
            <li key={channel.key} className="flex min-w-0 items-baseline justify-between gap-3">
              <span className="min-w-0 truncate">{channel.labelRu}</span>
              <span className="shrink-0 text-muted-foreground">{`— ${channel.modeRu}`}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="tabular-nums">{`Бюджет за ${formatDay(budget.day)}: ${formatInt(budget.dayRub)} из ${formatRub(budget.dayLimitRub)}`}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/launch" className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'max-sm:h-10')}>
          Открыть «Запуск»
        </Link>
        {canStop && <StopEverywhereButton onDone={onClose} />}
      </div>
    </>
  );
}

function ErrorDetails({ error }: { readonly error: unknown }) {
  return (
    <>
      <PopoverHeader>
        <PopoverTitle>Состояние Анны</PopoverTitle>
        <PopoverDescription>{messageRuOf(error)}</PopoverDescription>
      </PopoverHeader>
      <Link to="/readiness" className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'self-start max-sm:h-10')}>
        Открыть «Готовность»
      </Link>
    </>
  );
}

export function StatusPill({ variant, announce = false }: StatusPillProps) {
  const query = useQuery(statusQuery);
  const status = query.data;
  const announcement = useStoppedAnnouncement(status);
  const [open, setOpen] = useState(false);

  let view: PillView | null = null;
  if (status !== undefined) view = pillView(status);
  else if (isSettingsUnreadable(query.error)) {
    view = { text: SETTINGS_UNREADABLE_PILL_RU, word: 'Сбой настроек', tone: 'error', hollow: false };
  } else if (query.error !== null) {
    view = { text: 'Статус не прочитан', word: 'Нет статуса', tone: 'neutral', hollow: true };
  }

  const live = announce ? (
    <span className="sr-only" aria-live="assertive">
      {announcement}
    </span>
  ) : null;

  if (view === null) {
    return (
      <div aria-busy="true" className={cn(variant === 'sidebar' ? 'h-7 w-full' : variant === 'compact' ? 'h-11 w-full' : 'h-8 w-24', 'flex items-center')}>
        <Skeleton className="size-full" />
        <span className="sr-only">Статус Анны загружается</span>
      </div>
    );
  }

  const label = variant === 'sidebar' ? view.text : view.word;
  const details =
    status !== undefined ? (
      <StatusDetails
        status={status}
        onClose={() => {
          setOpen(false);
        }}
      />
    ) : (
      <ErrorDetails error={query.error} />
    );
  const content = (
    <PopoverContent side={variant === 'compact' ? 'right' : 'bottom'} align="start" className="w-80 gap-3">
      {details}
    </PopoverContent>
  );

  if (variant === 'compact') {
    // Сайдбар иконками: точка и слово под ней, полный текст («Тень · не работает») — в тултипе и подписи кнопки.
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger
            render={
              <PopoverTrigger
                aria-label={`Состояние Анны: ${view.text}`}
                className="btn flex w-full cursor-pointer flex-col items-center gap-1 rounded-md bg-muted px-1 py-1.5 text-xs text-foreground hover:bg-surface-hover aria-expanded:bg-surface-hover"
              />
            }
          >
            <StatusMark tone={view.tone} hollow={view.hollow} />
            <span aria-live={announce ? 'polite' : undefined} className="w-full text-center leading-3.5 text-balance">
              {view.word}
            </span>
          </TooltipTrigger>
          <TooltipContent side="right">{view.text}</TooltipContent>
        </Tooltip>
        {content}
        {live}
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label={variant === 'sidebar' ? undefined : `Состояние Анны: ${view.text}`}
        className={cn(
          'btn flex min-w-0 cursor-pointer items-center gap-1.5 rounded-md bg-muted text-sm text-foreground hover:bg-surface-hover aria-expanded:bg-surface-hover',
          variant === 'sidebar' && 'min-h-7 w-full px-2 py-1 text-left max-sm:min-h-10',
          variant === 'topbar' && 'h-8 max-w-40 px-2 max-sm:h-10',
        )}
      >
        <span aria-live={announce ? 'polite' : undefined} className="min-w-0 flex-1 truncate">
          <StatusDot tone={view.tone} hollow={view.hollow} label={label} size="sm" />
        </span>
        {variant === 'sidebar' && <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />}
      </PopoverTrigger>
      {content}
      {live}
    </Popover>
  );
}
